/**
 * Inline-editable duration cell for the Clockify-style timesheet grid.
 *
 * Shows the formatted duration as text. On click, switches to an
 * <input> that accepts "1:30" / "1.5" / "1,5". On blur or Enter,
 * parses the value and fires `onSave` with the new duration in seconds.
 * Escape reverts to the original value.
 */

import { useEffect, useRef, useState } from 'react';
import { InputBase } from '@mui/material';
import { colors } from '../../core/theme/theme';
import { formatDurationSeconds, parseHoursInput } from './timeEntries';

export interface TimesheetCellProps {
  /** Current aggregated seconds for this cell. */
  seconds: number;
  /** Called with the new total seconds when the user commits a change. */
  onSave: (newSeconds: number) => void;
  /**
   * Non-working day (weekend / public holiday) that the user has not opened
   * yet: the cell is read-only and a click unlocks the column instead of
   * starting an edit. Existing hours stay visible — they are never hidden.
   */
  locked?: boolean;
  onUnlock?: () => void;
}

/** Format seconds to the input display value (H:MM or empty). */
function toInputValue(seconds: number): string {
  if (seconds === 0) return '';
  return formatDurationSeconds(seconds);
}

export default function TimesheetCell({
  seconds,
  onSave,
  locked = false,
  onUnlock,
}: TimesheetCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync draft when entering edit mode or when external data changes
  // while not editing.
  useEffect(() => {
    if (!editing) setDraft(toInputValue(seconds));
  }, [seconds, editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();

    // Empty or "0" → clear the cell.
    if (trimmed === '' || trimmed === '0' || trimmed === '0:00') {
      if (seconds !== 0) onSave(0);
      return;
    }

    const hours = parseHoursInput(trimmed);
    if (hours === null) {
      // Invalid input — revert.
      setDraft(toInputValue(seconds));
      return;
    }

    const newSeconds = Math.round(hours * 3600);
    if (newSeconds !== seconds) {
      onSave(newSeconds);
    }
  };

  if (!editing || locked) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => (locked ? onUnlock?.() : setEditing(true))}
        onFocus={() => {
          if (!locked) setEditing(true);
        }}
        style={{
          display: 'block',
          boxSizing: 'border-box',
          textAlign: 'center',
          fontSize: 14,
          fontVariantNumeric: 'tabular-nums',
          color: seconds > 0 ? colors.bodyText : colors.textMuted,
          cursor: locked ? 'not-allowed' : 'text',
          // Permanent affordance: a faint box marks the cell as editable
          // even when empty (the grid is otherwise blank/white). Resting
          // hint placeholder "0:00" reinforces "type your hours here".
          border: locked ? '1px solid transparent' : `1px solid ${colors.lightGray}`,
          padding: '3px 0',
          minHeight: 28,
        }}
        data-testid="timesheet-cell-display"
        data-locked={locked ? 'true' : undefined}
      >
        {toInputValue(seconds) || (locked ? '' : '0:00')}
      </span>
    );
  }

  return (
    <InputBase
      inputRef={inputRef}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          setDraft(toInputValue(seconds));
          setEditing(false);
        }
      }}
      placeholder="0:00"
      inputProps={{
        size: 1, // override native default (size=20) so the field can shrink
        style: {
          textAlign: 'center',
          fontSize: 14,
          fontVariantNumeric: 'tabular-nums',
          padding: '4px 2px',
          minWidth: 0,
        },
        'data-testid': 'timesheet-cell-input',
      }}
      sx={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        border: `1px solid ${colors.kronosAccent}`,
        backgroundColor: colors.peach,
      }}
    />
  );
}
